import React from 'react';
import { Link } from 'react-router-dom';

// Simple SVG icon components (can be expanded or replaced with a library)
const TargetIcon = () => (
  <svg className="w-12 h-12 mx-auto mb-4 text-purple-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="6"></circle>
    <circle cx="12" cy="12" r="2"></circle>
  </svg>
);

const BoltIcon = () => (
  <svg className="w-12 h-12 mx-auto mb-4 text-purple-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
);

const ChartIcon = () => (
  <svg className="w-12 h-12 mx-auto mb-4 text-purple-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
    <line x1="18" y1="20" x2="18" y2="10"></line>
    <line x1="12" y1="20" x2="12" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="14"></line>
  </svg>
);

const CTAButton = ({ text }: { text: string }) => (
  <div className="text-center my-12 md:my-16">
    <Link
      to="/contact"
      className="bg-purple-600 hover:bg-purple-700 text-white px-8 md:px-10 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl uppercase inline-block transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-opacity-75 animate-wiggle animate-pulse-glow"
      style={{
        textShadow: '0 2px 4px rgba(0,0,0,0.4)',
      }}
    >
      {text}
    </Link>
  </div>
);

const CommitmentSection: React.FC = () => {
  return (
    <section className="py-16 md:py-20 bg-white text-gray-800">
      <div className="container mx-auto px-4">

        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-center mb-4 sm:mb-6 text-purple-700">
          We Don't Sell Promises.
        </h2>
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-center mb-8 md:mb-10 text-pink-500">
           We Deliver Buyers.
        </h2>

        <p className="text-lg md:text-xl lg:text-2xl text-center mb-12 md:mb-16 max-w-3xl mx-auto leading-relaxed text-gray-700">
          At IgniLead, we're not here to "help with marketing." We're here to fill your lot with qualified walk-ins using AI that doesn't sleep, doesn't flinch, and doesn't waste your ad spend.
        </p>

        <div className="mb-8 md:mb-10">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-10 md:mb-12 text-gray-800">
            We've Built an <span className="text-purple-600">Unfair Advantage</span> for Dealerships:
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 text-center">
              <TargetIcon />
              <h4 className="text-2xl font-semibold text-gray-800 mb-3">AI Pre-Qualification</h4>
              <p className="text-gray-600 leading-relaxed">AI that pre-qualifies leads before your sales team ever picks up the phone.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 text-center">
              <BoltIcon />
              <h4 className="text-2xl font-semibold text-gray-800 mb-3">Instant Response Systems</h4>
              <p className="text-gray-600 leading-relaxed">Instant response systems that cut no-shows by over 40%.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 text-center">
              <ChartIcon />
              <h4 className="text-2xl font-semibold text-gray-800 mb-3">Data-Backed Ads</h4>
              <p className="text-gray-600 leading-relaxed">Data-backed ads that auto-optimize to your most profitable cars—not just clicks.</p>
            </div>
          </div>
        </div>
        
        <CTAButton text="Get Your Unfair Advantage" />

        <div className="bg-gradient-to-br from-purple-600 to-pink-500 text-white p-8 md:p-10 lg:p-12 rounded-xl shadow-2xl mb-16 md:mb-20">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-black text-center mb-6 md:mb-8">
            We Only Win When <span className="underline decoration-yellow-400 decoration-4 underline-offset-8">You Win</span>
          </h3>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
             <div className="space-y-3 md:space-y-4 text-base md:text-lg lg:text-xl">
                <p className="flex items-start"><span className="text-yellow-400 mr-2 mt-1 flex-shrink-0">✓</span> We don't lock you into contracts.</p>
                <p className="flex items-start"><span className="text-yellow-400 mr-2 mt-1 flex-shrink-0">✓</span> We don't charge a dime unless we generate at least 10 test-drive-ready leads in the first 30 days.</p>
             </div>
             <div className="bg-white/20 backdrop-blur-sm p-4 md:p-6 rounded-lg text-center shadow-xl">
                <p className="text-xl md:text-2xl lg:text-3xl font-bold">
                    No leads, no money.
                </p>
                <p className="text-base md:text-lg lg:text-xl mt-2">That's how confident we are.</p>
             </div>
          </div>
        </div>

        <div className="bg-gray-800 text-white p-8 md:p-10 lg:p-12 rounded-xl shadow-2xl text-center">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4 md:mb-6">
            You're Not Early. <br className="sm:hidden" /> 
            <span className="text-pink-400 underline decoration-pink-400/70 decoration-wavy underline-offset-4">
                You're Already Behind.
            </span>
          </h3>
          <p className="text-lg md:text-xl text-gray-300 mb-3 md:mb-4 max-w-2xl mx-auto leading-relaxed">
            Your competitors are already exploring AI.
          </p>
          <p className="text-base md:text-lg text-gray-400 mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed">
            If you're still using old-school Facebook ads and hoping for floor traffic, you're bleeding revenue every day.
          </p>
          
          <CTAButton text="Start Winning With Us" />

        </div>

      </div>
    </section>
  );
};

export default CommitmentSection; 